import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch, useSelector } from 'react-redux';
import ReportIcon from '../../assets/icons/report-icon.svg?react';

import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Button from '../../components/Button';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { formatDate } from '../../utils/dateFormat';
// import { formatTime } from '../../utils/timeFomat';
import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import SaveAsDraft from '../../components/SaveAsDraft';
import { GetIncidentByIdResponse } from '@EHSNavigator/backend';

interface IncidentReportingStepThreeProps {
  otherClasses?: string;
  formHandler: any;
  isDraftLoading: boolean;
  handleSaveAsDraft: () => void;
  handleSteps: React.Dispatch<number>;
  departments: {
    label: string;
    value: string;
  }[];
  incidentTypes: {
    label: string;
    value: string;
  }[];
  attachments: GetIncidentByIdResponse['incident']['attachments'];
  disbaleButtons: boolean;
}

const IncidentReportingStepThree: React.FC<IncidentReportingStepThreeProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  handleSaveAsDraft,
  departments,
  incidentTypes,
  attachments,
  isDraftLoading,
  disbaleButtons,
}) => {
  const incidentReportingStepThreeClasses = clsx(
    otherClasses,
    'w-full flex flex-col flex-1 lg:gap-8',
  );
  const dispatch = useDispatch();
  const sizes = useButtonSizeSmaller();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const mergedFiles = [
    ...(formHandler?.values?.files || []),
    ...(attachments || []),
  ];
  return (
    <div
      className={incidentReportingStepThreeClasses}
      data-testid="incident-reporting-step-three"
    >
      <div className="flex flex-col lg:gap-10 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex max-w-818 flex-1 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <p className="font-inter-variable text-brand-byzantine -tracking-form-label mb-1 text-xs leading-4 font-bold lg:hidden">
              Report an Incident
            </p>
            <h1 className="h3 mb-1 lg:mb-0">Review Details</h1>
            <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              STEP 3 / 3
            </p>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Review the incident details. By submitting, you agree to the terms
              and confirm the report is truthful and accurate.
            </p>
          </div>
        </div>
        <SaveAsDraft
          handleSaveAsDraft={handleSaveAsDraft}
          isDisabled={isDraftLoading}
          disbaleButtons={disbaleButtons}
        />
      </div>
      <div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-input mt-15 text-base leading-5 font-normal italic lg:hidden">
          Review the incident details. By submitting, you agree to the terms and
          confirm the report is truthful and accurate.
        </p>
        <div className="lg:border-t-neutral-tones-text/10 py-8 lg:border-t">
          <div className="mb-8">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-2.5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Title
            </h6>
            <h3 className="text-neutral-tones-text text-3xl capitalize">
              {formHandler?.values?.title}
            </h3>
          </div>
          <div className="grid max-w-950 grid-cols-1 items-start gap-9 md:grid-cols-2 md:gap-12 lg:gap-100">
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Date & Time
              </h6>
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                {!formHandler?.values?.dateTimeUndetermined ? (
                  <>
                    <div className="flex flex-col gap-2.5">
                      <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                        Date
                      </h6>
                      <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                        {formatDate(formHandler?.values?.date)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2.5">
                      <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                        Time
                      </h6>
                      <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                        {formHandler?.values?.time}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                      Date & Time
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                      Undetermined
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Location of Incident
              </h6>
              <div className="flex items-start gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Location Address
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal lowercase first-letter:uppercase">
                    {formHandler?.values?.factory}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-y-neutral-tones-text/10 border-y py-8">
          <div className="grid max-w-950 grid-cols-1 items-start gap-9 md:grid-cols-2 md:gap-12 lg:gap-100">
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Department Involved
              </h6>
              <div className="grid grid-cols-2 gap-x-75 gap-y-6 md:gap-x-12 lg:gap-x-75 lg:gap-y-[50px]">
                <div className="col-span-1 flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Department
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {
                      departments?.find(
                        (node) =>
                          node?.value === formHandler?.values?.department,
                      )?.label
                    }
                  </p>
                </div>
                <div className="col-span-1 flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Contractor Involvement
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.contractorInvolved}
                  </p>
                </div>
                {formHandler?.values?.affectedBodyParts?.length > 0 && (
                  <div className="col-span-2 flex flex-col gap-2.5">
                    <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                      Affected Body Parts
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                      {formHandler?.values?.affectedBodyParts?.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Severity
              </h6>
              <div className="flex flex-wrap items-start gap-x-75 gap-y-6 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5 md:w-1/2">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Type
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {
                      incidentTypes?.find(
                        (node) =>
                          node?.value === formHandler?.values?.incidentType,
                      )?.label
                    }
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Severity
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.incidentSeverity}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Were multiple people injured?
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.multiplePeopleInjured ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-864 py-8">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
              Incident Description
            </h6>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
              <div className="flex flex-col gap-2.5 md:col-span-8">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Description
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {formHandler?.values?.description}
                </p>
              </div>
              <div className="flex flex-col gap-2.5 md:col-span-4">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Images/Videos Uploaded
                </h6>
                {mergedFiles?.length === 0 ? (
                  <p className="font-inter-variable text-neutral-tones-text/40 -tracking-form-input text-base leading-5 font-normal">
                    No images uploaded
                  </p>
                ) : (
                  <div className="mt-2.5 flex flex-col gap-2">
                    {mergedFiles?.map((node, index: number) => {
                      return (
                        <p
                          key={index}
                          className="font-inter-variable text-brand-byzantine -tracking-form-input text-base leading-5 font-normal"
                        >
                          {node?.name}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-8">
        <Button
          onClick={() => handleSteps(2)}
          size={sizes}
          type="button"
          variant="secondary"
          otherClasses="max-w-211 w-full"
          disable={disbaleButtons}
        >
          <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" /> Go
          Back
        </Button>
        <Button
          type="button"
          onClick={() => dispatch(toggleModal('submit-modal'))}
          size={sizes}
          disable={disbaleButtons}
          otherClasses="max-w-211 w-full"
        >
          Proceed{' '}
          <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>

      {modalName === 'submit-modal' && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass="w-full max-w-573 rounded-2xl "
        >
          <div className="flex w-full flex-col items-center gap-12 px-9 py-6">
            <div className="flex flex-col items-center gap-2.5 text-center">
              <ReportIcon className="[&>path]:fill-brand-byzantine h-85 w-85" />
              <h4 className="text-brand-byzantine">Confirm Submission</h4>
              <p className="font-inter-variable text-brand-byzantine -tracking-text-area text-lg leading-5 font-normal">
                Clicking submit will register a report and an email will be sent
                to the site management that a incident has been created.
              </p>
            </div>
            <div className="flex w-full flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
              <Button
                size="big"
                onClick={() => dispatch(toggleModal(''))}
                variant="secondary"
                otherClasses="w-full"
              >
                Cancel
              </Button>
              <Button
                disable={formHandler?.isSubmitting}
                loading={formHandler?.isSubmitting}
                onClick={() => {
                  formHandler?.handleSubmit();
                }}
                otherClasses="w-full"
                size="big"
              >
                Submit Report
              </Button>
            </div>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default IncidentReportingStepThree;
